import { useEffect, useMemo, useState } from 'react';
import { useUserSettings } from '../../../../utils/use-user-settings';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import {
  ActionErrorEntry,
  createDecomModel,
  DecomFormValues,
  getActionLabel,
  getDecomFormDefaultValues,
  useCreateDecomMutation,
  useEditDecomMutation,
} from './decom-policy-utils';
import { pagesConfig } from '../../../../shared/pages-config';
import { useQuery } from '@tanstack/react-query';
import { getAllDecomModels, UserKeys } from '../../../../api/users-api';
import { Messages } from '../../../../utils/message-constants';
import {
  Control,
  FieldErrors,
  SubmitHandler,
  useForm,
  UseFormClearErrors,
  UseFormGetValues,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
} from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import FormSkeleton from './decom-policy-form-skeleton';
import {
  isAbleToSeeUIElement,
  ToolsRoles,
} from '../../../../utils/roles/client-roles.ts';
import BasicInfoStep from './form-steps/basic-info-step.tsx';
import ActionsStep from './form-steps/actions-step.tsx';
import DayActionStep from './form-steps/action-run-step.tsx';
import { DecomModel } from '../../../../../types.ts';
import ReviewStep from './form-steps/review-step.tsx';

import UnsavedChangesDialog, {
  useUnsavedChanges,
} from '../../../../shared/unsaved-changes-dialog';
import {
  FormSubSteps,
  StepConfig,
} from '../../../../shared/shared-form-stepper.tsx';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import SimpleFormPageNew from '../../../../shared/simple-form-page-new.tsx';
import {
  CreateDaySchema,
  DecomActionConfigSchema,
  DecomBasicInfoSchema,
  DecomPolicyFormSchema,
} from './decom-policy-form-schema.tsx';

interface DecomFormPageProps {
  title: string;
  pageMode: string;
}
interface StepProps {
  control: Control<DecomPolicyFormValues>;
  errors: FieldErrors<DecomPolicyFormValues>;
  setValue: UseFormSetValue<DecomPolicyFormValues>;
  getValues: UseFormGetValues<DecomPolicyFormValues>;
  watch: UseFormWatch<DecomPolicyFormValues>;
  trigger: UseFormTrigger<DecomPolicyFormValues>;
  clearErrors: UseFormClearErrors<DecomPolicyFormValues>;
  decomModels: DecomModel[];
}
//  Type referencing Schema for the whole decom form page
// (DecomForm extends DecomModel)
export type DecomPolicyFormValues = z.infer<typeof DecomPolicyFormSchema>;
export default function DecomFormPage({ title, pageMode }: DecomFormPageProps) {
  const { id: selectedRowId } = useParams();
  const [actionSubSteps, setActionSubSteps] = useState<FormSubSteps[]>([]);

  const { data: decomModels, isLoading } = useQuery({
    queryKey: [UserKeys.DecomModels],
    queryFn: () => getAllDecomModels(),
    meta: {
      errorMessage: Messages.Errors.Network.Loading('decom models'),
    },
  });
  const navigate = useNavigate();
  const { loggedInUser, changeLogRoles } = useUserSettings();
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);

  const selectedDecom = decomModels?.find(
    (policy) => policy.id === selectedRowId
  );

  const dynamicSchema = useMemo(() => {
    return DecomPolicyFormSchema.superRefine((data, ctx) => {
      if (decomModels) {
        const existingOus = decomModels
          .filter((m) => m.id !== selectedDecom?.id)
          .flatMap((m) => m.org || [])
          .map((ou) => ou.toLowerCase());
        const dataOus = data.org || [];
        dataOus.forEach((ou) => {
          if (existingOus.includes(ou.toLowerCase())) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `Organization Unit '${ou}' is already assigned to another policy.`,
              path: ['org'],
            });
          }
        });
      }
    });
  }, [decomModels, selectedDecom?.id]);

  const {
    control,
    handleSubmit,
    formState: { errors, isValidating, isSubmitting, isDirty, touchedFields },
    trigger,
    reset,
    getValues,
    setValue,
    watch,
    clearErrors,
  } = useForm<DecomPolicyFormValues>({
    resolver: zodResolver(dynamicSchema),
    defaultValues: getDecomFormDefaultValues({
      pageMode,
      selectedDecom,
      loggedInUser,
    }),
  });

  const customDaysRaw = watch('customDays');
  const customDays = useMemo(() => customDaysRaw ?? [], [customDaysRaw]);
  const actionsDataRaw = watch('actions');
  const actionsData = useMemo(() => actionsDataRaw || [], [actionsDataRaw]);
  const [activeStep, setActiveStep] = useState(0);
  const [dayInputErrors, setDayInputErrors] = useState<Record<number, string>>(
    {}
  );

  const [actionTypesWithErrors, setActionTypesWithErrors] = useState<
    ActionErrorEntry[]
  >([]);

  const selectedPolicy = getValues();

  const [manualCompleted, setManualCompleted] = useState<boolean[]>([
    false,
    false,
  ]);

  const steps = useMemo(() => {
    const baseSteps: StepConfig[] = [
      {
        id: 'basic-info',
        label: 'Basic Information',
        description:
          'Please provide a policy name, a user to send notifications to upon action completions, and choose your decom trigger type. "User Suspension" policies are triggered if the user is in one of the selected org units AND they are suspended. "Org Unit Change" policies are triggered if a user is moved into any of the selected org units.',
        schema: DecomBasicInfoSchema,
      },
      {
        id: 'actions',
        label: 'Action Configuration',
        description:
          'Configure timeline days out buckets as well as some default actions that will affect all selected actions.',
        schema: DecomActionConfigSchema,
      },
    ];

    const customDaySteps = Array.from(new Set(customDays))
      .sort((a, b) => a - b)
      .map((day) => ({
        id: `day-${day}`,
        label:
          day === 0
            ? 'First Run Configuration'
            : `${day} Day${day === 1 ? '' : 's'} Out`,
        description: '',
        schema: CreateDaySchema(day),
        actionTypes: actionsData[day].actions
          .map((a) => {
            const id = a.actionType ?? '';
            const label = getActionLabel(id);
            return label ? { id, label } : null;
          })
          .filter(
            (item): item is { id: string; label: string } => item !== null
          ),
      }));

    const reviewStep: StepConfig = {
      id: 'review',
      label: 'Review',
      description:
        'Below is a summary of your policy selections. Please confirm that all the information displayed is correct before proceeding with policy creation.',
    };

    if (pageMode !== 'create')
      setManualCompleted(() =>
        [...baseSteps, ...customDaySteps, reviewStep].map(() => true)
      );
    return [...baseSteps, ...customDaySteps, reviewStep];
  }, [customDays, actionsData, pageMode]);

  function renderStepComponent(step: StepConfig, props: StepProps) {
    switch (step.label) {
      case 'Basic Information':
        return <BasicInfoStep {...props} />;
      case 'Action Configuration':
        return (
          <ActionsStep
            {...props}
            dayInputErrors={dayInputErrors}
            setDayInputErrors={setDayInputErrors}
            touchedFields={touchedFields}
          />
        );
      case 'Review':
        return (
          <ReviewStep pageMode={pageMode} selectedPolicy={selectedPolicy} />
        );
      default:
        if (step.id && step.id.startsWith('day-')) {
          const day = parseInt(step.id.replace('day-', ''), 10);
          return (
            <DayActionStep
              day={day}
              {...props}
              actionTypesWithErrors={actionTypesWithErrors}
              setActionTypesWithErrors={setActionTypesWithErrors}
              setActionSubSteps={setActionSubSteps}
            />
          );
        }
        return null;
    }
  }

  const handleCancel = () => {
    navigate(`/${pagesConfig.decommissioningLayout.link}`, {
      state: { fromCancelButton: true },
    });
  };

  const { mutate, isPending } = useCreateDecomMutation({
    onClearChangeLog: () => setChangeLogMessage(''),
    onCancel: () => {
      bypass();
      navigate(`/${pagesConfig.decommissioningLayout.link}`);
    },
    onResetForm: () => reset(),
    onResetStepIndex: () => setActiveStep(0),
  });

  const { mutate: editMutate, isPending: editIsPending } = useEditDecomMutation(
    selectedRowId,
    {
      onClearChangeLog: () => setChangeLogMessage(''),
      onCancel: () => {
        bypass();
        navigate(`/${pagesConfig.decommissioningLayout.link}`);
      },
      onResetForm: () => reset(),
      onResetStepIndex: () => setActiveStep(0),
    }
  );

  const onSubmit: SubmitHandler<DecomFormValues> = (data) => {
    const decomModel = createDecomModel(data);

    if (pageMode === 'create' || pageMode === 'clone') {
      if (
        isAbleToSeeUIElement(
          changeLogRoles,
          ToolsRoles.CreateDecommissioningProcesses.value
        )
      ) {
        setOpenChangeLogDialog(true);
      } else {
        mutate(decomModel);
      }
    } else {
      if (
        isAbleToSeeUIElement(
          changeLogRoles,
          ToolsRoles.EditDecommissioningProcesses.value
        )
      ) {
        setOpenChangeLogDialog(true);
      } else {
        editMutate(decomModel);
      }
    }
  };
  const CurrentStepComponent =
    steps[activeStep] !== undefined
      ? renderStepComponent(steps[activeStep], {
          control,
          errors,
          setValue,
          getValues,
          watch,
          trigger,
          clearErrors,
          decomModels: decomModels ?? [],
        })
      : null;

  const formStepperProps = {
    actionSubSteps,
    activeStep,
    setActiveStep,
    steps,
    errors,
    trigger,
    stepCompleted: manualCompleted,
    setStepCompleted: setManualCompleted,
    subStep: actionSubSteps,
  };

  // Set value to form
  useEffect(() => {
    if ((pageMode === 'edit' || pageMode === 'clone') && selectedDecom) {
      reset(
        getDecomFormDefaultValues({
          pageMode,
          selectedDecom,
          loggedInUser,
        })
      );
    }
  }, [selectedDecom, pageMode, reset, loggedInUser]);

  const {
    showDialog: showUnsavedChangesDialog,
    handleConfirm: handleUnsavedConfirm,
    handleCancel: handleUnsavedCancel,
    bypass,
    isCancelAction,
  } = useUnsavedChanges(isDirty);

  return (
    <>
      <SimpleFormPageNew
        title={title}
        {...formStepperProps}
        content={
          isLoading || (selectedRowId && !selectedDecom) ? (
            <FormSkeleton />
          ) : (
            CurrentStepComponent
          )
        }
        handleDiscard={handleCancel}
        handleSubmit={handleSubmit(onSubmit)}
        isLoadingSubmit={
          isValidating || isSubmitting || isPending || editIsPending
        }
        status={
          pageMode === 'edit'
            ? selectedDecom && selectedDecom.isActive
              ? 'Active'
              : 'Disabled'
            : undefined
        }
      />

      {/* Cancel Confirmation Dialog */}
      <UnsavedChangesDialog
        isCancelAction={isCancelAction}
        entityName="Decommissioning Policy"
        pageMode={pageMode}
        open={showUnsavedChangesDialog}
        handleConfirm={handleUnsavedConfirm}
        handleClose={handleUnsavedCancel}
      />

      <ChangeLogDialog
        open={openChangeLogDialog}
        handleClose={() => setOpenChangeLogDialog(false)}
        changeLogMessage={changeLogMessage}
        setChangeLogMessage={setChangeLogMessage}
        mutate={() => {
          const decomModel = createDecomModel({ ...getValues() });
          mutate(decomModel);
        }}
        mutateLoading={isPending}
      />
    </>
  );
}
